/**
 * What the signed-in person may do (SPEC.md §8.1, §10.1).
 *
 * The same shape as the access token in `shared/api/http.ts`, and for the same reason. The auth
 * store owns *who is signed in*; this holds the one fact about them that the whole application
 * asks — may they do this? — so that a component does not have to reach up into a feature to find
 * out. §10.1 forbids that reach: `shared` may not know about `features`, and one feature may not
 * know about another.
 *
 * **This hides buttons; it never authorises anything.** Every endpoint checks the same permission
 * server-side and refuses independently (ADR-005: hidden must mean refused by the server). A
 * control the API still honours is not a feature that is off; it is a feature somebody will find.
 *
 * @example
 * import { can } from '@/shared/lib/permissions'
 * const mayCancel = computed(() => can('documents.cancel'))
 */
import { ref } from 'vue'

/**
 * The set itself, and there is exactly one of it.
 *
 * The auth store exposes this very ref as its own `permissions` rather than keeping a copy in step
 * with it. Two copies is how a test that assigns `store.permissions` leaves `can()` answering from
 * the other one — which is not a hypothetical, it is what the first version of this module did.
 *
 * Read it through `can()`. It is exported because the store owns it, not as an invitation.
 */
export const grantedPermissions = ref<ReadonlySet<string>>(new Set())

/**
 * The signed-in role (`ADMIN`, `MANAGER`, …), or `null` when nobody is signed in.
 *
 * Held here beside the permissions, and kept in step by the auth store, for the one rule a
 * permission cannot express: who may cancel a posted document depends on the role and on how long
 * ago it was posted (SPEC.md §7.3, `shared/lib/cancelPolicy.ts`), and the payment table that asks
 * lives in a feature that may not import the auth feature (§10.1).
 */
export const grantedRole = ref<string | null>(null)

/** Replace the set. The auth store does this on sign-in, on refresh and on sign-out. */
export function setPermissions(next: Iterable<string>): void {
  grantedPermissions.value = new Set(next)
}

/** True when the signed-in person holds `permission`. Reactive inside a `computed`. */
export function can(permission: string): boolean {
  return grantedPermissions.value.has(permission)
}
